import {
  createEmptyCard,
  fsrs,
  Rating,
  State,
  type CardInput,
  type RecordLogItem,
  type ReviewLog,
} from "ts-fsrs";

export type ReviewOutcome = "again" | "hard" | "good" | "easy" | "never";

export type FsrsState =
  | State.New
  | State.Learning
  | State.Review
  | State.Relearning;

export type FsrsReviewState = {
  due: string;
  stability: number;
  difficulty: number;
  elapsedDays: number;
  scheduledDays: number;
  learningSteps: number;
  reps: number;
  lapses: number;
  fsrsState: FsrsState;
  lastReviewDate: string | null;
};

type FsrsReviewLog = {
  rating: Rating;
  state: State;
  due: string;
  stability: number;
  difficulty: number;
  elapsedDays: number;
  lastElapsedDays: number;
  scheduledDays: number;
  learningSteps: number;
  reviewedAt: string;
  result: FsrsReviewState;
};

type Result = FsrsReviewState & {
  nextReviewDate: Date;
  reviewLog: FsrsReviewLog | null;
};

export const NEVER_SHOW_CARD_INTERVAL = 36500;

const FSRS_PARAMETERS = {
  request_retention: 0.95,
  learning_steps: ["10m", "1d"],
  relearning_steps: ["10h"],
  enable_fuzz: false,
} as const;

const scheduler = fsrs(FSRS_PARAMETERS);
const dayMs = 24 * 60 * 60 * 1000;

const outcomeToRating = {
  again: Rating.Again,
  hard: Rating.Hard,
  good: Rating.Good,
  easy: Rating.Easy,
} satisfies Record<Exclude<ReviewOutcome, "never">, Rating>;

const toDate = (value: Date | string | number): Date => {
  return value instanceof Date ? value : new Date(value);
};

const addDays = (date: Date, days: number): Date => {
  return new Date(date.getTime() + days * dayMs);
};

const roundDays = (days: number): number => {
  return parseFloat(days.toFixed(2));
};

export const getDaysUntilDue = (
  now: Date | string,
  due: Date | string,
): number => {
  return roundDays((toDate(due).getTime() - toDate(now).getTime()) / dayMs);
};

const isFsrsReviewState = (
  review: Partial<FsrsReviewState> | undefined,
): review is FsrsReviewState => {
  return (
    !!review &&
    review.due !== undefined &&
    review.stability !== undefined &&
    review.difficulty !== undefined &&
    review.elapsedDays !== undefined &&
    review.scheduledDays !== undefined &&
    review.learningSteps !== undefined &&
    review.reps !== undefined &&
    review.lapses !== undefined &&
    review.fsrsState !== undefined
  );
};

export const createInitialFsrsReviewState = (now: Date): FsrsReviewState => {
  const card = createEmptyCard(now);
  return cardToReviewState(card);
};

const toCardInput = (
  now: Date,
  review: Partial<FsrsReviewState> | undefined,
): CardInput => {
  if (isFsrsReviewState(review)) {
    return {
      due: review.due,
      stability: review.stability,
      difficulty: review.difficulty,
      elapsed_days: review.elapsedDays,
      scheduled_days: review.scheduledDays,
      learning_steps: review.learningSteps,
      reps: review.reps,
      lapses: review.lapses,
      state: review.fsrsState,
      last_review: review.lastReviewDate,
    };
  }

  return createEmptyCard(now);
};

const cardToReviewState = (card: CardInput): FsrsReviewState => {
  const due = toDate(card.due);
  return {
    due: due.toISOString(),
    stability: card.stability,
    difficulty: card.difficulty,
    elapsedDays: card.elapsed_days,
    scheduledDays: card.scheduled_days,
    learningSteps: card.learning_steps,
    reps: card.reps,
    lapses: card.lapses,
    fsrsState: card.state as FsrsState,
    lastReviewDate: card.last_review
      ? toDate(card.last_review).toISOString()
      : null,
  };
};

const reviewLogToDbLog = (
  log: ReviewLog,
  result: FsrsReviewState,
): FsrsReviewLog => {
  return {
    rating: log.rating,
    state: log.state,
    due: log.due.toISOString(),
    stability: log.stability,
    difficulty: log.difficulty,
    elapsedDays: log.elapsed_days,
    lastElapsedDays: log.last_elapsed_days,
    scheduledDays: log.scheduled_days,
    learningSteps: log.learning_steps,
    reviewedAt: log.review.toISOString(),
    result,
  };
};

const resultFromRecordLogItem = (item: RecordLogItem): Result => {
  const result = cardToReviewState(item.card);
  return {
    ...result,
    nextReviewDate: toDate(result.due),
    reviewLog: reviewLogToDbLog(item.log, result),
  };
};

const createManualLog = (
  now: Date,
  previous: CardInput,
  result: FsrsReviewState,
): FsrsReviewLog => {
  return {
    rating: Rating.Manual,
    state: previous.state as State,
    due: toDate(previous.due).toISOString(),
    stability: previous.stability,
    difficulty: previous.difficulty,
    elapsedDays: previous.elapsed_days,
    lastElapsedDays: previous.elapsed_days,
    scheduledDays: previous.scheduled_days,
    learningSteps: previous.learning_steps,
    reviewedAt: now.toISOString(),
    result,
  };
};

const preserveReviewState = (
  now: Date,
  review: Partial<FsrsReviewState>,
): Result => {
  const previous = toCardInput(now, review);
  const previousScheduledDays = previous.scheduled_days;
  const due = addDays(now, previousScheduledDays);
  const result = cardToReviewState({
    ...previous,
    due,
    scheduled_days: previousScheduledDays,
    last_review: now,
  });

  return {
    ...result,
    nextReviewDate: due,
    reviewLog: createManualLog(now, previous, result),
  };
};

const hideReviewState = (
  now: Date,
  review: Partial<FsrsReviewState> | undefined,
): Result => {
  const previous = toCardInput(now, review);
  const due = addDays(now, NEVER_SHOW_CARD_INTERVAL);
  const result = cardToReviewState({
    ...previous,
    due,
    scheduled_days: NEVER_SHOW_CARD_INTERVAL,
    last_review: now,
    state: State.Review,
  });

  return {
    ...result,
    nextReviewDate: due,
    reviewLog: createManualLog(now, previous, result),
  };
};

export const reviewCard = (
  now: Date | string,
  previousReview: Partial<FsrsReviewState> | undefined,
  reviewOutcome: ReviewOutcome,
  isInterrupted = false,
  isStudyAnyway = false,
): Result => {
  const reviewDate = toDate(now);

  if (reviewOutcome === "never") {
    return hideReviewState(reviewDate, previousReview);
  }

  if (isStudyAnyway && previousReview?.lastReviewDate) {
    return preserveReviewState(reviewDate, previousReview);
  }

  if (isInterrupted && reviewOutcome === "again") {
    const previous = toCardInput(reviewDate, previousReview);
    const result = cardToReviewState({
      ...previous,
      due: reviewDate,
      scheduled_days: 0,
      last_review: reviewDate,
      state: State.Relearning,
    });

    return {
      ...result,
      nextReviewDate: reviewDate,
      reviewLog: createManualLog(reviewDate, previous, result),
    };
  }

  const previous = toCardInput(reviewDate, previousReview);
  return resultFromRecordLogItem(
    scheduler.next(previous, reviewDate, outcomeToRating[reviewOutcome]),
  );
};

export const previewReviewCard = (
  now: Date | string,
  previousReview: Partial<FsrsReviewState> | undefined,
  reviewOutcome: Exclude<ReviewOutcome, "never">,
): Result => {
  const reviewDate = toDate(now);
  const previous = toCardInput(reviewDate, previousReview);
  return resultFromRecordLogItem(
    scheduler.next(previous, reviewDate, outcomeToRating[reviewOutcome]),
  );
};
